import { Link } from "react-router-dom";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  ExternalLink,
  Download,
} from "lucide-react";
import { COMPANY } from "@/data/company";
import { PRODUCT_CATEGORIES } from "@/data/products";
import NagoLogo from "@/components/NagoLogo";
import { COMPANY_PROFILE_PDF } from "@/components/DownloadProfileButton";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      className="mt-16 bg-slate-900 text-slate-300"
      data-testid="site-footer"
    >
      {/* Top red accent bar */}
      <div className="h-1 bg-[#E30613]" />
      <div className="container-nago py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <NagoLogo className="h-14 w-auto bg-white p-2 rounded-lg" />
          <p className="mt-5 text-sm text-slate-400 leading-relaxed max-w-sm">
            One reliable source for industrial supply — lubricants, tooling,
            safety, packaging and custom machines, delivered across India.
          </p>
          <a
            href={COMPANY_PROFILE_PDF}
            download="Nago-International-Company-Profile.pdf"
            target="_blank"
            rel="noreferrer"
            className="mt-5 inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/15 hover:border-[#E30613] text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200"
            data-testid="footer-download-profile-btn"
          >
            <Download className="w-4 h-4 text-[#E30613]" />
            Download Company Profile
          </a>
          <div className="mt-6 space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-[#E30613] mt-0.5 shrink-0" />
              <span className="text-slate-400">{COMPANY.address}</span>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="w-4 h-4 text-[#E30613] mt-0.5 shrink-0" />
              <div className="text-slate-400">
                {COMPANY.phones.map((p) => (
                  <div key={p}>
                    <a
                      href={`tel:${p.replace(/\s/g, "")}`}
                      className="hover:text-white"
                    >
                      {p}
                    </a>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Mail className="w-4 h-4 text-[#E30613] mt-0.5 shrink-0" />
              <a
                href={`mailto:${COMPANY.email}`}
                className="text-slate-400 hover:text-white"
              >
                {COMPANY.email}
              </a>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="w-4 h-4 text-[#E30613] mt-0.5 shrink-0" />
              <span className="text-slate-400">{COMPANY.hours}</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <h4 className="text-white font-display font-bold text-sm uppercase tracking-widest mb-4">
            Quick Links
          </h4>
          <ul className="space-y-2 text-sm">
            {[
              { to: "/", label: "Home" },
              { to: "/about", label: "About Us" },
              { to: "/products", label: "Products" },
              { to: "/brands", label: "Brands" },
              { to: "/clients", label: "Clients" },
              { to: "/contact", label: "Contact" },
            ].map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="text-slate-400 hover:text-white"
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-3">
          <h4 className="text-white font-display font-bold text-sm uppercase tracking-widest mb-4">
            Products
          </h4>
          <ul className="space-y-2 text-sm">
            {PRODUCT_CATEGORIES.slice(0, 6).map((p) => (
              <li key={p.slug}>
                <Link
                  to={`/products/${p.slug}`}
                  className="text-slate-400 hover:text-white"
                >
                  {p.title.replace(" & ", " & ")}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-3">
          <h4 className="text-white font-display font-bold text-sm uppercase tracking-widest mb-4">
            Registrations
          </h4>
          <div className="text-sm text-slate-400 space-y-2">
            <div>
              GSTIN:{" "}
              <span className="text-white font-mono">{COMPANY.gstin}</span>
            </div>
            <div>
              Udyam:{" "}
              <span className="text-white font-mono">{COMPANY.udyam}</span>
            </div>
            <a
              href={COMPANY.mapsLink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 mt-3 text-[#93C5FD] hover:text-white"
              data-testid="footer-maps-link"
            >
              View on Google Maps <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-800">
        <div className="container-nago py-5 flex flex-col md:flex-row md:items-center justify-between gap-3 text-xs text-slate-500">
          <div>
            © {year} Nago International. All rights reserved. | Developed and
            maintained by{" "}
            <a
              href="https://www.linkedin.com/in/ganesh-dandale"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white underline underline-offset-2 transition-colors"
              data-testid="footer-developer"
            >
              Ganesh Dandale
            </a>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link
              to="/terms"
              className="hover:text-white"
              data-testid="footer-terms"
            >
              Terms &amp; Conditions
            </Link>
            <Link
              to="/privacy-policy"
              className="hover:text-white"
              data-testid="footer-privacy"
            >
              Privacy Policy
            </Link>
            <Link
              to="/disclaimer"
              className="hover:text-white"
              data-testid="footer-disclaimer"
            >
              Disclaimer
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
